import React, { useMemo } from 'react';
import { Marker } from 'react-map-gl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVanShuttle, faWrench } from '@fortawesome/free-solid-svg-icons';
import { Avatar } from 'components/layout/Avatar';
import { MapAppointmentType, MapEmployeeType, MapToPlanJobType } from '../SchedulingTypes';

interface MapMarkersProps {
    appointments: MapAppointmentType[];
    toPlanJobs: MapToPlanJobType[];
    employees: MapEmployeeType[];
    focusedAppointmentId: number | null;
    onClickAppointment: (appointment: MapAppointmentType) => void;
    onClickToPlanJob: (job: MapToPlanJobType) => void;
    onHoverAppointment: (appointment: MapAppointmentType | null, x: number, y: number) => void;
    onHoverToPlanJob: (job: MapToPlanJobType | null, x: number, y: number) => void;
    onHoverEmployee: (employee: MapEmployeeType | null, x: number, y: number) => void;
}

// Pixel radius used to fan out pins that share the exact same coordinates
const OVERLAP_SPREAD_RADIUS = 16;

// Number of minutes after which an employee marker is shown greyed out
const EMPLOYEE_STALE_AFTER_MINUTES = 15;

// Returns wheter an employee position is stale, based on its last gps time
export const isEmployeeStale = (lastGpsAt: string) => {
    const minutesAgo = (Date.now() - new Date(lastGpsAt).getTime()) / 60000;
    return minutesAgo >= EMPLOYEE_STALE_AFTER_MINUTES;
};

// Map an employee status to its badge icon
const EMPLOYEE_STATUS_ICONS: Record<string, typeof faWrench> = {
    traveling: faVanShuttle,
    working: faWrench,
};

const MapMarkers: React.FC<MapMarkersProps> = ({
    appointments, toPlanJobs, employees, focusedAppointmentId,
    onClickAppointment, onClickToPlanJob, onHoverAppointment, onHoverToPlanJob, onHoverEmployee
}) => {

    // Calculate a pixel offset per pin, fanning out pins that share the same coordinates
    // in a small circle so they all stay visible and clickable
    const pinOffsets = useMemo(() => {
        const offsets = new Map<string, [number, number]>();

        // Group all pin keys by their exact coordinates
        const groups = new Map<string, string[]>();
        appointments
            .filter(appointment => appointment.work_location_latitude && appointment.work_location_longitude)
            .forEach(appointment => {
                const coordinateKey = `${appointment.work_location_latitude},${appointment.work_location_longitude}`;
                groups.set(coordinateKey, [...(groups.get(coordinateKey) ?? []), `appointment-${appointment.id}`]);
            });
        toPlanJobs
            .filter(job => job.work_location_latitude && job.work_location_longitude)
            .forEach(job => {
                const coordinateKey = `${job.work_location_latitude},${job.work_location_longitude}`;
                groups.set(coordinateKey, [...(groups.get(coordinateKey) ?? []), `to-plan-job-${job.id}`]);
            });

        // Group the employees by their exact coordinates, separate from the job pin groups above
        const employeeGroups = new Map<string, string[]>();
        employees
            .filter(employee => employee.latitude && employee.longitude)
            .forEach(employee => {
                const coordinateKey = `employee-${employee.latitude},${employee.longitude}`;
                employeeGroups.set(coordinateKey, [...(employeeGroups.get(coordinateKey) ?? []), `employee-${employee.id}`]);
            });

        // Spread the pins of each group with more than one pin in a circle
        [groups, employeeGroups].forEach(groupMap => {
            groupMap.forEach(pinKeys => {
                if (pinKeys.length < 2) return;
                pinKeys.forEach((pinKey, index) => {
                    const angle = (2 * Math.PI * index) / pinKeys.length;
                    offsets.set(pinKey, [Math.round(Math.cos(angle) * OVERLAP_SPREAD_RADIUS), Math.round(Math.sin(angle) * OVERLAP_SPREAD_RADIUS)]);
                });
            });
        });

        return offsets;
    }, [appointments, toPlanJobs, employees]);

    return (
        <>
            {/* Render pins for the appointments within the displayed date range, colored by job status */}
            {appointments
                .filter(appointment => appointment.work_location_latitude && appointment.work_location_longitude)
                .map(appointment => (
                    <Marker
                        key={`appointment-${appointment.id}`}
                        longitude={parseFloat(appointment.work_location_longitude!)}
                        latitude={parseFloat(appointment.work_location_latitude!)}
                        anchor="bottom"
                        offset={pinOffsets.get(`appointment-${appointment.id}`)}
                        onClick={(e) => { e.originalEvent.stopPropagation(); onClickAppointment(appointment); }}
                    >
                        <div
                            className={`scheduler-map-pin scheduler-map-pin-appointment ${appointment.job_status_condition ?? ''} ${focusedAppointmentId === appointment.id ? 'selected' : ''}`}
                            onMouseEnter={(e) => onHoverAppointment(appointment, e.clientX, e.clientY)}
                            onMouseLeave={() => onHoverAppointment(null, 0, 0)}
                        />
                    </Marker>
                ))}

            {/* Render pins for the jobs that are not yet planned, colored by job status */}
            {toPlanJobs
                .filter(job => job.work_location_latitude && job.work_location_longitude)
                .map(job => (
                    <Marker
                        key={`to-plan-job-${job.id}`}
                        longitude={parseFloat(job.work_location_longitude!)}
                        latitude={parseFloat(job.work_location_latitude!)}
                        anchor="bottom"
                        offset={pinOffsets.get(`to-plan-job-${job.id}`)}
                        onClick={(e) => { e.originalEvent.stopPropagation(); onClickToPlanJob(job); }}
                    >
                        <div
                            className={`scheduler-map-pin scheduler-map-pin-to-plan ${job.status_condition ?? ''}`}
                            onMouseEnter={(e) => onHoverToPlanJob(job, e.clientX, e.clientY)}
                            onMouseLeave={() => onHoverToPlanJob(null, 0, 0)}
                        />
                    </Marker>
                ))}

            {/* Render pins for the current employee positions, with an avatar and a status badge */}
            {employees
                .filter(employee => employee.latitude && employee.longitude)
                .map(employee => (
                    <Marker
                        key={`employee-${employee.id}`}
                        longitude={parseFloat(employee.longitude!)}
                        latitude={parseFloat(employee.latitude!)}
                        anchor="center"
                        offset={pinOffsets.get(`employee-${employee.id}`)}
                    >
                        <div
                            className={`scheduler-map-employee-pin ${employee.status} ${isEmployeeStale(employee.last_gps_at) ? 'stale' : ''}`}
                            onMouseEnter={(e) => onHoverEmployee(employee, e.clientX, e.clientY)}
                            onMouseLeave={() => onHoverEmployee(null, 0, 0)}
                        >
                            {employee.image ? (
                                <div className="scheduler-map-employee-avatar-image" style={{ backgroundImage: `url(${employee.image})` }} />
                            ) : (
                                <Avatar name={employee.name} size="2.25rem" />
                            )}
                            {/* Only show a status badge for a known status, so no meaningless dot is rendered */}
                            {EMPLOYEE_STATUS_ICONS[employee.status] && (
                                <span className={`scheduler-map-employee-status-badge ${employee.status}`}>
                                    <FontAwesomeIcon icon={EMPLOYEE_STATUS_ICONS[employee.status]} />
                                </span>
                            )}
                        </div>
                    </Marker>
                ))}
        </>
    );
};

export default MapMarkers;
